/** @jsx jsx */
import keycode from 'keycode';
import React from 'react';
import { jsx } from '@emotion/core';
import { get, isFunction } from 'lodash';

import { AccordionPart, AccordionParts } from './AccordionPart';
import AccordionArrow from './AccordionArrow';
import AccordionHeader from './AccordionHeader';
import AccordionContent from './AccordionContent';

export type AccordionHeaderProps = {
  isOpen: boolean;
  toggleAccordion: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

type AccordionHeaderRendererProps = {
  children: (api: AccordionHeaderProps) => any;
  isOpen: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
  toggleAccordion: () => void;
};

type AccordionBodyProps = {
  children?: any;
};

function AccordionBody(props: AccordionBodyProps) {
  return <AccordionPart part="body">{props.children}</AccordionPart>;
}

function AccordionHeaderWrapper(_props: {
  children: (props: AccordionHeaderProps) => any;
}) {
  return null;
}

function AccordionHeaderRenderer(props: AccordionHeaderRendererProps) {
  return (
    <AccordionPart part="header">
      {isFunction(props.children) &&
        props.children({
          isOpen: props.isOpen,
          onKeyDown: props.onKeyDown,
          toggleAccordion: props.toggleAccordion,
        })}
    </AccordionPart>
  );
}

/** Types */
type AccordionProps = {
  /** Open on mount? */
  isOpen: boolean;
  /** Children */
  children: React.ReactNode;
};
type AccordionState = {
  isOpen: boolean;
};

/**
 * Displays an Accordion
 *
 * Its header and content are fully configurable via:
 * - `<Accordion.Header>`
 * - `<Accordion.Body>`
 *
 * We're using a _"Bring Your Own Header"_ approach to provide maximum flexibility to any consumer.
 *
 * `Accordion.Header` should use render props, it exposes `isOpen`, `onKeyDown`, and `toggleAccordion`
 */
class Accordion extends React.Component<AccordionProps, AccordionState> {
  state: AccordionState = {
    isOpen: this.props.isOpen,
  };
  static defaultProps = {
    isOpen: false,
  };
  static Arrow = AccordionArrow;
  static Body = AccordionBody;
  static Header = AccordionHeaderWrapper;

  handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.keyCode) {
      case keycode('enter'):
      case keycode('space'):
        this.toggleAccordion();
        break;
    }
  };

  toggleAccordion = () =>
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
    }));

  renderAccordionBody = (): React.ReactNode => this.props.children;

  renderAccordionHeader = (): React.ReactNode =>
    React.Children.toArray(this.props.children).map((element, index) => {
      const props = {
        key: index,
        isOpen: this.state.isOpen,
        onKeyDown: this.handleKeyDown,
        toggleAccordion: this.toggleAccordion,
      };
      const elementProps = get(element, 'props', {});
      return <AccordionHeaderRenderer {...props} {...elementProps} />;
    });

  render() {
    const { isOpen } = this.state;

    // dts-bundle-generator absolutely refused to play nicely with the existing
    // filter solution we had above, so I took a different approach:
    //
    // 1. Create the AccordionParts wrapper which renders children within a context
    // 2. Said context knows what part should be rendered
    // 3. An AccordionPart only renders itself if its part prop matches the containing AccordionParts part prop
    return (
      <div>
        <AccordionParts part="header">
          <AccordionHeader isOpen={isOpen}>
            {this.renderAccordionHeader()}
          </AccordionHeader>
        </AccordionParts>
        <AccordionParts part="body">
          <AccordionContent isOpen={isOpen}>
            <div>{this.renderAccordionBody()}</div>
          </AccordionContent>
        </AccordionParts>
      </div>
    );
  }
}

export default Accordion;
